import React, { useState } from 'react';
import {
  CalendarDaysIcon,
  ClockIcon,
  ArrowsRightLeftIcon,
  UserIcon,
  ChartBarIcon,
  DocumentDuplicateIcon
} from '@heroicons/react/24/outline';
import { useAuth } from '../contexts/AuthContext';
import EnhancedDashboard from './EnhancedDashboard';
import DragDropShiftCalendar from '../components/Scheduling/DragDropShiftCalendar';
import ShiftTemplates from '../components/Scheduling/ShiftTemplates';
import EmployeeAvailability from '../components/Scheduling/EmployeeAvailability';
import ShiftSwaps from '../components/Scheduling/ShiftSwaps';

const EnterpriseScheduling = () => {
  const [activeTab, setActiveTab] = useState('dashboard');
  const { user, isAdmin, isManager } = useAuth();

  const tabs = [
    {
      key: 'dashboard',
      label: 'Dashboard',
      icon: ChartBarIcon,
      description: 'Overview and analytics'
    },
    {
      key: 'calendar',
      label: 'Shift Calendar',
      icon: CalendarDaysIcon,
      description: 'Drag & drop scheduling',
      roles: ['admin', 'hr_manager', 'manager']
    },
    {
      key: 'templates',
      label: 'Shift Templates',
      icon: DocumentDuplicateIcon,
      description: 'Reusable shift patterns',
      roles: ['admin', 'hr_manager', 'manager']
    },
    {
      key: 'availability',
      label: 'Availability',
      icon: UserIcon,
      description: 'Employee availability management'
    },
    {
      key: 'swaps',
      label: 'Shift Swaps',
      icon: ArrowsRightLeftIcon,
      description: 'Employee shift exchanges'
    }
  ];

  const filteredTabs = tabs.filter(tab => {
    if (!tab.roles) return true;
    return tab.roles.includes(user?.role);
  });

  const renderTabContent = () => {
    switch (activeTab) {
      case 'dashboard':
        return <EnhancedDashboard />;
      case 'calendar':
        return <DragDropShiftCalendar />;
      case 'templates':
        return <ShiftTemplates />;
      case 'availability':
        return <EmployeeAvailability />;
      case 'swaps':
        return <ShiftSwaps />;
      default:
        return <EnhancedDashboard />;
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-white shadow rounded-lg">
        <div className="px-6 py-4 border-b border-gray-200">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Enterprise Scheduling</h1>
              <p className="mt-1 text-sm text-gray-500">
                Comprehensive UK-compliant scheduling and workforce management
              </p>
            </div>
            <div className="flex items-center space-x-2">
              <div className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                <ClockIcon className="h-3 w-3 mr-1" />
                UK Compliance Active
              </div>
            </div>
          </div>
        </div>

        {/* Tab Navigation */}
        <div className="px-6">
          <nav className="-mb-px flex space-x-8">
            {filteredTabs.map((tab) => {
              const IconComponent = tab.icon;
              return (
                <button
                  key={tab.key}
                  onClick={() => setActiveTab(tab.key)}
                  className={`group inline-flex items-center py-4 px-1 border-b-2 font-medium text-sm ${
                    activeTab === tab.key
                      ? 'border-primary-500 text-primary-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  }`}
                >
                  <IconComponent
                    className={`-ml-0.5 mr-2 h-5 w-5 ${
                      activeTab === tab.key
                        ? 'text-primary-500'
                        : 'text-gray-400 group-hover:text-gray-500'
                    }`}
                  />
                  <div className="text-left">
                    <div>{tab.label}</div>
                    <div className="text-xs text-gray-400 hidden sm:block">
                      {tab.description}
                    </div>
                  </div>
                </button>
              );
            })}
          </nav>
        </div>
      </div>

      {/* Tab Content */}
      <div className="min-h-[600px]">
        {renderTabContent()}
      </div>

      {/* Footer Info */}
      <div className="bg-blue-50 border border-blue-200 rounded-md p-4">
        <div className="flex">
          <div className="flex-shrink-0">
            <ClockIcon className="h-5 w-5 text-blue-400" />
          </div>
          <div className="ml-3">
            <h3 className="text-sm font-medium text-blue-800">
              UK Working Time Regulations Compliance
            </h3>
            <div className="mt-2 text-sm text-blue-700">
              <p>
                This system enforces UK Working Time Regulations including daily rest periods (11 hours), 
                weekly rest periods (24/48 hours), maximum working hours (48 hour average), 
                and mandatory rest breaks for shifts over 6 hours.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default EnterpriseScheduling;